/**
 * SportIcon — icon for each supported sport.
 *
 * Default (lucide) mode keeps the original API used by cards/alerts:
 *    <SportIcon sport="Basketball" className="h-3 w-3" />
 *    <SportIcon sport="Soccer" withLabel />
 *
 * Line mode renders a custom line glyph that inherits `currentColor`
 * (so it can be tinted grey / by board type):
 *    <SportIcon sport="Basketball" variant="line" size={16} />
 */
import { CircleDot, Circle, Target, Volleyball, Award, Zap, Snowflake, Sparkles, Waves, Music, Swords } from "lucide-react";

// Map every sport to (icon, color). Colors match the app palette.
const MAP = {
    Basketball:   { Icon: CircleDot,  color: "#ff4500" },
    Soccer:       { Icon: Circle,     color: "#22c55e" },
    Baseball:     { Icon: Target,     color: "#ef4444" },
    Volleyball:   { Icon: Volleyball, color: "#f59e0b" },
    Softball:     { Icon: Target,     color: "#facc15" },
    Football:     { Icon: Award,      color: "#a78bfa" },
    "Flag Football": { Icon: Award,   color: "#c4b5fd" },
    "Mixed Martial Arts": { Icon: Swords, color: "#dc2626" },
    Lacrosse:     { Icon: Zap,        color: "#38bdf8" },
    "Ice Hockey": { Icon: Snowflake,  color: "#e5e5e5" },
    Gymnastics:   { Icon: Sparkles,   color: "#f472b6" },
    Swimming:     { Icon: Waves,      color: "#06b6d4" },
    Cheer:        { Icon: Music,      color: "#ec4899" },
};

// Custom line glyphs (stroke = currentColor). Keyed by normalized sport name.
const KEY = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
const GLYPHS = {
    basketball: <><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18" /><path d="M5.6 5.6C8 8 8 16 5.6 18.4M18.4 5.6C16 8 16 16 18.4 18.4" /></>,
    soccer: <><circle cx="12" cy="12" r="9" /><path d="M12 8.4l3 2.2-1.15 3.6h-3.7L9 10.6z" /><path d="M12 8.4V3.2M15 10.6l5.3-2M13.85 14.2l3.3 4.6M10.15 14.2l-3.3 4.6M9 10.6l-5.3-2" /></>,
    baseball: <><circle cx="12" cy="12" r="9" /><path d="M8.4 4C6.6 6.3 6.6 17.7 8.4 20" /><path d="M15.6 4c1.8 2.3 1.8 13.7 0 16" /><path d="M6.7 7.2l1.5.5M6.4 10.4l1.6.3M6.4 13.6l1.6-.3M6.7 16.8l1.5-.5M17.3 7.2l-1.5.5M17.6 10.4l-1.6.3M17.6 13.6l-1.6-.3M17.3 16.8l-1.5-.5" /></>,
    softball: <><circle cx="12" cy="12" r="9" /><path d="M8.4 4C6.6 6.3 6.6 17.7 8.4 20" /><path d="M15.6 4c1.8 2.3 1.8 13.7 0 16" /><path d="M6.7 7.2l1.5.5M6.4 10.4l1.6.3M6.4 13.6l1.6-.3M6.7 16.8l1.5-.5M17.3 7.2l-1.5.5M17.6 10.4l-1.6.3M17.6 13.6l-1.6-.3M17.3 16.8l-1.5-.5" /></>,
    volleyball: <><circle cx="12" cy="12" r="9" /><path d="M12 3v18" /><path d="M12 12c-3-1.4-6.5-1.2-8.8.4M12 12c3-1.4 6.5-1.2 8.8.4" /><path d="M6.5 4.6C8.4 8 8.4 16 6.5 19.4M17.5 4.6C15.6 8 15.6 16 17.5 19.4" /></>,
    football: <><ellipse cx="12" cy="12" rx="8.6" ry="5.4" /><path d="M6 9.6v4.8M18 9.6v4.8" /><path d="M9 12h6M11.4 10.6v2.8M12.6 10.6v2.8" /></>,
    flagfootball: <><path d="M6 4v16" /><path d="M6 5.2c3-1.6 6 1.6 9 0v6.2c-3 1.6-6-1.6-9 0z" /></>,
    mixedmartialarts: <><path d="M7 8.5c0-1.7 1.2-2.8 3-2.8h3.2c1.8 0 3 1.1 3 2.8v3.8c0 1.9-1.2 3-3 3H9" /><path d="M7 9.2C5.7 9.2 5 10 5 11.2s.7 2 2 2" /><path d="M9 15.2V18h6v-3" /></>,
    icehockey: <><path d="M4.5 4.5l9 12M13.5 16.5l3 .9" /><path d="M19.5 4.5l-9 12M10.5 16.5l-3 .9" /><ellipse cx="12" cy="20.2" rx="2.5" ry="1" /></>,
    gymnastics: <><circle cx="13" cy="5" r="2" /><path d="M13 7c-1.6 1.4-4 1.8-5.8 1M13 7c1.8.6 3 2.2 3.4 4" /><path d="M12 8.5l-2.4 4.5 1 4M12.5 9l2.6 4-.4 4.5" /></>,
    swimming: <><circle cx="8.5" cy="7" r="1.8" /><path d="M4 11c1.6-1 3-1 4.4.2l2.2 1.8c1.4 1.1 2.9 1.1 4.5 0L21 9" /><path d="M3 17c1.5-1.3 3-1.3 4.5 0s3 1.3 4.5 0 3-1.3 4.5 0 3 1.3 4.5 0" /></>,
    cheer: <><g transform="rotate(-18 12 12)"><path d="M5 13v-2L15 7.5v9z" /><path d="M8 14v2.4l2.4.6V15" /><path d="M17 8l2-1M18 12h2.2M17 16l2 1" /></g></>,
    lacrosse: <><path d="M6.5 19.5L14.5 8.8" /><path d="M14.5 8.8c-2-1.5-1.5-4.7 1.1-5.6 2.5-.9 5 1.1 4.2 3.9-.7 2.3-3.2 3.2-5.3 1.7z" /><path d="M15 5.6l3.6 2.3M16.3 4.3l2.5 3.2" /><circle cx="6.3" cy="19.6" r="1.3" /></>,
};

export default function SportIcon({ sport, className = "h-4 w-4", withLabel = false, testid, variant = "lucide", size = 16, style = {} }) {
    if (variant === "line") {
        const children = GLYPHS[KEY(sport)];
        if (!children) return null;
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" className={className} style={style} aria-hidden="true"
                data-testid={testid || `sport-icon-${KEY(sport)}`}>
                {children}
            </svg>
        );
    }
    const entry = MAP[sport] || MAP.Basketball;
    const { Icon, color } = entry;
    if (withLabel) {
        return (
            <span
                data-testid={testid || `sport-icon-${sport}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color }}
            >
                <Icon className={className} style={{ color }} strokeWidth={2.5} />
                {sport}
            </span>
        );
    }
    return (
        <Icon
            data-testid={testid || `sport-icon-${sport}`}
            className={className}
            style={{ color }}
            strokeWidth={2.5}
        />
    );
}

export const SPORT_COLOR = Object.fromEntries(Object.entries(MAP).map(([k, v]) => [k, v.color]));
